/**
 * Feature E07-F04 — Review screen (the core manual-verification table).
 *
 * 1:1 port of the prototype `../EER app/prototype/assets/research.proto.js`
 * (renderReview L872-1008, the filters / breakdown-legend strips, setReview /
 * editGrade / overrideGrade L1010-1028). This is the thin React shell: all the
 * presentation rules (score-monotonic displayRank, grade/status filter combine,
 * count badges, chip palettes, row tint, score-bar) live in the DOM-free,
 * unit-tested `./researchReviewModel.ts`; the wire shapes + hooks come from
 * `./researchReviewApi.ts`. No `innerHTML` — every cell is React-escaped.
 *
 * Tasks realised here:
 *   T05 — the long-list table: #/Company/Score/Grade/Breakdown/Evidence/Accept-
 *         Reject columns, Grade (All/A/B/C) + Status (All/Accepted/Rejected/
 *         Pending) filters that COMBINE, the score-sort toggle, the summary strip
 *         (total · accepted · needs-human · "showing X of total (N discovered)"),
 *         the 6-square breakdown legend, amber tint on needs_human rows, sticky
 *         header + scroll-area + a pager (50 rows/page by default, 100/200/All;
 *         any filter/sort change returns to page 1 — `paginateRows`);
 *   T06 — the editable grade chip: a read-only coloured pill (A green / B blue /
 *         C gray; null → "—") that turns into an A/B/C <select> on click, persists
 *         on change/blur (the server audits the human override) and re-renders;
 *   T08 — the displayRank is computed by `filteredRankedRows` on the FULL list
 *         BEFORE the filter, so the # stays score-monotonic 1..N and manual
 *         unscored rows sink to the bottom.
 *   T14 — Tile (sub-industry × geo × size) and Industry columns, a filter per
 *         facet (drop-downs with counts) and sort by either (click the header).
 *
 * Wire-up (the serial Wire pass mounts this as the `review` research screen in the
 * shell). The view is self-contained: it takes the `requestId` to review and an
 * optional `onBack` / `onPublish` for the shell to route the breadcrumb.
 */
import { Fragment, useMemo, useState, useRef, useEffect, type JSX } from 'react';
import {
  REVIEW_GRADES,
  isUnresolvedCompanyName,
  type ReviewRow,
  type ReviewGrade,
  type ReviewGradeFilter,
  type ReviewStatusFilter,
  type ScoreExplanation,
} from '@eer/shared';
import {
  filteredRankedRows,
  insufficientDataBadge,
  locationCells,
  computeSummary,
  facetLabel,
  facetKey,
  facetOptions,
  nextSort,
  REVIEW_FACET_ALL,
  gradeChipStyle,
  verdictChipStyle,
  rowTintClass,
  scoreBarWidth,
  REVIEW_LEGEND,
  REVIEW_CRITERION_ORDER,
  initialReviewFilter,
  ungradedNotice,
  DEFAULT_REVIEW_PAGE_SIZE,
  REVIEW_PAGE_SIZES,
  paginateRows,
  isPrefiltered,
  prefilteredCount,
  withDefaultTriageSort,
  type FacetOption,
  type ReviewPage,
  type ReviewPageSize,
  type ReviewFacet,
  type ReviewFilterState,
  type ReviewSortBy,
} from './researchReviewModel.js';
import {
  useReviewList,
  useSetReviewStatus,
  useOverrideGrade,
  useVerifyCompany,
  useSubmitReview,
  useDismissPrefilter,
  useLinkRowCompany,
} from './researchReviewApi.js';
// E07-F11-T02 / T03 — Jev triage column, bucket filter, run controls, same-company hints.
import {
  TRIAGE_FILTER_ALL,
  hasTriage,
  sameCompanyHintViews,
  triageCell,
  triageFilterOptions,
  type ReviewTriageFilter,
} from './researchTriageModel.js';
import { ReviewTriageBar } from './ReviewTriageBar.js';
import { EvidenceDrawer } from './EvidenceDrawer.js';
import { ReviewDiscoveryWarning } from './DiscoveryIssuesBanner.js';
import { ReviewDiscoveryTiles } from './DiscoveryTilesTable.js';
import { ReviewDiscoveryExclusions } from './DiscoveryExclusionsList.js';
// E07-F04-T16 — the request's original criteria (shared with the Run screen).
import { ReviewRequestCriteria } from './RequestCriteriaPanel.js';
// E07-F04-T18 — the request's Grade scope sets the default grade filter.
import { useResearchStatus } from './researchOrchestratorApi.js';
// E07-F04-T09 — one label rule for the Review table and the AI-check screen.
import { CompanyName } from './CompanyLabel.js';
import { ReviewWebsiteCell } from './ReviewWebsiteCell.js';
import { AddCompanyDialog, BulkAddDialog } from './ReviewAddDialogs.js';
import { ScoreExplainToggle } from './ScoreExplain.js';
import { reviewRowExplanation } from './scoreExplainModel.js';
import { useToast } from '../ui/ToastContext.js';
// E07-F04-T12 — "information collected <date> — refresh?" + the paid per-row Refresh.
import { PriorCollectionNote } from './PriorCollectionNote.js';
import { useRerunAiOne } from './researchAiChainApi.js';
import { ENTER_WEBSITE_FIRST, aiRunErrorMessage, rowWebsite } from './referenceSiteModel.js';

/** Column count of the review table — the score-explanation row spans all of them. */
const REVIEW_TABLE_COLSPAN = 14;

// ---------------------------------------------------------------------------
// public props
// ---------------------------------------------------------------------------

export interface ResearchReviewViewProps {
  /** The research_request whose long-list is under review. */
  readonly requestId: number;
  /** Back to the Run screen (optional — the shell routes the breadcrumb). */
  readonly onBack?: () => void;
  /** Go to Publish (only meaningful once the gate is passed). */
  readonly onPublish?: () => void;
  /** E07-F04-T12 — open an earlier request (the "collected in" link of a row). */
  readonly onOpenRequest?: (requestId: number, title: string) => void;
  /**
   * E06-F02-T11 — back to the list the request was opened from (History / Library,
   * with its search/filter intact). "Back to run" is a flow step to THIS request's
   * Run screen; this one is the real way back to the list.
   */
  readonly onBackToList?: () => void;
  /** E06-F02-T11 — caption of the back-to-list button (names the panel). */
  readonly backToListLabel?: string;
  /**
   * E06-F02-T12 — the generic "Back": to the screen the recruiter came from (the
   * host walks its Research history), shown first in the back row.
   */
  readonly onBackPrevious?: () => void;
}

// ---------------------------------------------------------------------------
// filter pill (prototype `fbtn` — navy active / white inactive)
// ---------------------------------------------------------------------------

function FilterPill({
  label,
  active,
  testId,
  onClick,
}: {
  label: string;
  active: boolean;
  testId: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium transition ${
        active
          ? 'bg-slate-800 text-white'
          : 'bg-white text-gray-500 hover:bg-slate-50 border border-gray-200'
      }`}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// E07-F04-T14 — facet filter (tile / industry drop-down with row counts)
// ---------------------------------------------------------------------------

function FacetSelect({
  facet,
  label,
  value,
  options,
  onChange,
}: {
  facet: ReviewFacet;
  label: string;
  value: string;
  options: readonly FacetOption[];
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <label className="flex items-center gap-1 ml-3">
      <span className="text-xs font-bold text-slate-400 uppercase mr-1">{label}</span>
      <select
        data-testid={`${facet}-filter`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`text-xs border rounded px-2 py-1 max-w-[16rem] ${
          value === REVIEW_FACET_ALL
            ? 'border-gray-200 text-slate-600 bg-white'
            : 'border-slate-800 text-slate-900 bg-slate-50 font-medium'
        }`}
      >
        <option value={REVIEW_FACET_ALL}>All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label} ({o.count})
          </option>
        ))}
      </select>
    </label>
  );
}

/** A sortable column header (T14): click sorts by it; a second click flips. */
function SortHeader({
  by,
  label,
  filter,
  onSort,
}: {
  by: ReviewSortBy;
  label: string;
  filter: ReviewFilterState;
  onSort: (by: ReviewSortBy) => void;
}): JSX.Element {
  const active = (filter.sortBy ?? 'score') === by;
  return (
    <button
      type="button"
      data-testid={`review-sort-${by}`}
      data-active={active ? 'true' : 'false'}
      data-sort-dir={active ? filter.sortDir : ''}
      onClick={() => onSort(by)}
      className={`uppercase font-bold text-xs hover:text-slate-800 ${active ? 'text-slate-800' : ''}`}
      title={`Sort by ${label.toLowerCase()}`}
    >
      {label}
      {active ? (
        <i
          className={`fa-solid ml-1 ${filter.sortDir === 'asc' ? 'fa-arrow-up-a-z' : 'fa-arrow-down-z-a'}`}
          aria-hidden
        />
      ) : null}
    </button>
  );
}

// ---------------------------------------------------------------------------
// T05 — pager (a 100-440-row long-list is paged; page size 50/100/200/All)
// ---------------------------------------------------------------------------

function ReviewPager({
  view,
  pageSize,
  onPage,
  onPageSize,
}: {
  view: ReviewPage;
  pageSize: ReviewPageSize;
  onPage: (page: number) => void;
  onPageSize: (size: ReviewPageSize) => void;
}): JSX.Element {
  const btn =
    'px-2 py-1 border border-gray-200 rounded text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent';
  return (
    <div
      data-testid="review-pager"
      data-page={view.page}
      data-page-count={view.pageCount}
      className="px-4 py-2 border-t border-gray-100 flex flex-wrap items-center gap-2 text-xs text-slate-500"
    >
      <span data-testid="review-page-info">
        Rows <b>{view.from}</b>–<b>{view.to}</b> of {view.total}
      </span>
      <label className="flex items-center gap-1 ml-3">
        <span>Per page</span>
        <select
          data-testid="review-page-size"
          value={String(pageSize)}
          onChange={(e) =>
            onPageSize(
              e.target.value === 'all' ? 'all' : (Number(e.target.value) as ReviewPageSize),
            )
          }
          className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white"
        >
          {REVIEW_PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
          <option value="all">All</option>
        </select>
      </label>
      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          data-testid="review-page-first"
          disabled={view.page <= 1}
          onClick={() => onPage(1)}
          className={btn}
          aria-label="First page"
        >
          <i className="fa-solid fa-angles-left" aria-hidden />
        </button>
        <button
          type="button"
          data-testid="review-page-prev"
          disabled={view.page <= 1}
          onClick={() => onPage(view.page - 1)}
          className={btn}
          aria-label="Previous page"
        >
          <i className="fa-solid fa-angle-left" aria-hidden />
        </button>
        <span data-testid="review-page-current" className="px-2">
          Page {view.page} of {view.pageCount}
        </span>
        <button
          type="button"
          data-testid="review-page-next"
          disabled={view.page >= view.pageCount}
          onClick={() => onPage(view.page + 1)}
          className={btn}
          aria-label="Next page"
        >
          <i className="fa-solid fa-angle-right" aria-hidden />
        </button>
        <button
          type="button"
          data-testid="review-page-last"
          disabled={view.page >= view.pageCount}
          onClick={() => onPage(view.pageCount)}
          className={btn}
          aria-label="Last page"
        >
          <i className="fa-solid fa-angles-right" aria-hidden />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// score cell (heat-bar — prototype scoreCell L832-838)
// ---------------------------------------------------------------------------

/**
 * E06-F05-T07: the number is never bare. The whole cell is the button that opens
 * the weighted breakdown (what the % is made of + why the company is on the list at
 * all); hovering already surfaces the one-line headline via `title`.
 * E07-F04-T21: the click opens the Evidence drawer (score explanation on top, then
 * the per-criterion quotes + points). The old inline table row opened below the
 * fold / off the horizontally-scrolled table, so "click for the full breakdown"
 * looked like it did nothing.
 * An unscored row keeps its "—" but stays clickable — the panel then explains that
 * no AI check has run yet, which is the other half of the reported confusion.
 */
function ScoreCell({
  score,
  rowId,
  explanation,
  open,
  onToggle,
}: {
  score: number | null;
  rowId: number;
  explanation: ScoreExplanation;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <ScoreExplainToggle
      explanation={explanation}
      rowId={rowId}
      open={open}
      onToggle={onToggle}
      opensDialog
      className="justify-center"
    >
      {score == null ? (
        <span className="text-base font-bold text-slate-300" data-testid="score-cell">
          —
        </span>
      ) : (
        <span className="flex flex-col items-center gap-1" data-testid="score-cell">
          <span className="text-base font-bold text-slate-900 leading-none">{score}</span>
          <span className="block w-10 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <span
              className="block h-full rounded-full bg-slate-700"
              style={{ width: `${scoreBarWidth(score)}%` }}
            />
          </span>
        </span>
      )}
    </ScoreExplainToggle>
  );
}

// ---------------------------------------------------------------------------
// mini breakdown (6 squares — prototype miniBreakdown L853-861)
// ---------------------------------------------------------------------------

function MiniBreakdown({ row }: { row: ReviewRow }): JSX.Element {
  return (
    <div className="flex gap-1" data-testid="breakdown-squares">
      {REVIEW_CRITERION_ORDER.map((key) => {
        const verdict = row.breakdown[key];
        const style = verdictChipStyle(verdict);
        const legend = REVIEW_LEGEND.find((l) => l.key === key);
        return (
          <span
            key={key}
            data-testid={`breakdown-${key}`}
            title={`${legend?.label ?? key}: ${(verdict ?? 'unclear').toUpperCase()}`}
            className={`inline-flex items-center justify-center w-5 h-5 rounded text-[9px] ${style.className}`}
          >
            {style.symbol}
          </span>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// editable grade chip (T06) — read-only pill → A/B/C select on click
// ---------------------------------------------------------------------------

function GradeChip({
  row,
  onOverride,
}: {
  row: ReviewRow;
  onOverride: (grade: ReviewGrade) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);
  const style = gradeChipStyle(row.grade);

  // Focus the select the moment it opens (prototype `sel.focus()`).
  useEffect(() => {
    if (editing) selectRef.current?.focus();
  }, [editing]);

  if (editing) {
    return (
      <select
        ref={selectRef}
        data-testid={`grade-select-${row.id}`}
        defaultValue={row.grade ?? 'B'}
        onChange={(e) => {
          onOverride(e.target.value as ReviewGrade);
          setEditing(false);
        }}
        // persist-on-blur: closing the dropdown re-renders the read-only chip.
        onBlur={() => setEditing(false)}
        className="text-xs border border-slate-300 bg-white font-bold rounded px-1 py-0.5 focus:ring-2 focus:ring-slate-400 outline-none"
      >
        {REVIEW_GRADES.map((g) => (
          <option key={g} value={g}>
            {g}
          </option>
        ))}
      </select>
    );
  }

  return (
    <button
      type="button"
      data-testid={`grade-chip-${row.id}`}
      data-grade={row.grade ?? ''}
      title={
        !row.grade && row.checkState === 'not_checked'
          ? 'No grade yet — the AI check has not run for this company · click to set a grade'
          : 'Grade = fit to your request criteria · click to override'
      }
      onClick={() => setEditing(true)}
      className="rounded-full focus:outline-none focus:ring-2 focus:ring-slate-400"
    >
      <span
        className={`inline-flex items-center justify-center ${
          style.isPlaceholder ? 'px-1.5 h-6' : 'w-6 h-6'
        } rounded-full ${style.isPlaceholder ? 'text-[10px] font-medium' : 'text-xs font-bold'} ring-1 ${style.className}`}
      >
        {style.label}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// E07-F11-T02 — the «Triage» cell (bucket chip + code-built reason / progress / error)
// ---------------------------------------------------------------------------

function TriageCell({ row }: { row: ReviewRow }): JSX.Element {
  const cell = triageCell(row);
  if (cell.kind === 'none') {
    return (
      <span className="text-xs text-slate-300" data-testid={`triage-cell-${row.id}`}>
        —
      </span>
    );
  }
  return (
    <div
      data-testid={`triage-cell-${row.id}`}
      data-triage-kind={cell.kind}
      data-triage-bucket={cell.bucket ?? ''}
      className="flex flex-col gap-0.5"
    >
      <span
        className={`inline-flex items-center gap-1 self-start text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap ${cell.chipClass}`}
      >
        {cell.kind === 'running' || cell.kind === 'queued' ? (
          <i className="fa-solid fa-spinner fa-spin" aria-hidden />
        ) : null}
        {cell.label}
      </span>
      {cell.detail ? (
        <span
          className={`text-[10px] leading-tight ${cell.kind === 'unavailable' ? 'text-red-600' : 'text-slate-500'}`}
          data-testid={`triage-reason-${row.id}`}
        >
          {cell.detail}
        </span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// E07-F11-T03 — Jev same-company hint + the human «Link»
// ---------------------------------------------------------------------------

function SameCompanyHints({
  row,
  onLink,
  linkingCompanyId,
}: {
  row: ReviewRow;
  onLink?: (companyId: number) => void;
  linkingCompanyId: number | null;
}): JSX.Element | null {
  const hints = sameCompanyHintViews(row);
  if (hints.length === 0) return null;
  return (
    <div className="flex flex-col gap-0.5 mt-0.5" data-testid={`same-company-hints-${row.id}`}>
      {hints.map((h) => (
        <div
          key={h.companyId}
          className="flex items-center gap-1.5 text-[11px] text-indigo-700"
          data-testid={`same-company-hint-${row.id}-${h.companyId}`}
          data-relation={h.relation}
        >
          <i className="fa-solid fa-code-merge text-indigo-400" aria-hidden />
          <span title="Jev hint — nothing changes until you click Link">{h.text}</span>
          {onLink ? (
            <button
              type="button"
              data-testid={`same-company-link-${row.id}-${h.companyId}`}
              disabled={linkingCompanyId !== null}
              onClick={() => onLink(h.companyId)}
              title={`Link this row to ${h.companyName} in the Library`}
              className="text-[10px] px-1 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
            >
              {linkingCompanyId === h.companyId ? 'Linking…' : 'Link'}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// a single long-list row
// ---------------------------------------------------------------------------

function ReviewTableRow({
  requestId,
  row,
  onSetStatus,
  onOverrideGrade,
  onVerify,
  onOpenEvidence,
  verifying,
  evidenceOpen,
  onRefreshPrior,
  refreshBusy,
  onOpenRequest,
  onReturnPrefilter,
  returning = false,
  selected = false,
  onToggleSelect,
  onLinkCompany,
  linkingCompanyId = null,
}: {
  /** E07-F11-T02 — the row is ticked for «Re-triage selected». */
  selected?: boolean;
  onToggleSelect?: () => void;
  /** E07-F11-T03 — «Link» a Jev same-company hint (a person's click). */
  onLinkCompany?: (companyId: number) => void;
  linkingCompanyId?: number | null;
  /** E06-F03-T13-S03 — for the manual-website PATCH. */
  requestId: number;
  /** E07-F11-T01 — «Return» a prefilter-flagged row (clears its flags). */
  onReturnPrefilter?: () => void;
  returning?: boolean;
  /** E07-F04-T12 — PAID re-collection of this company (only from the click). */
  onRefreshPrior: () => void;
  refreshBusy: boolean;
  onOpenRequest?: (requestId: number, title: string) => void;
  row: ReviewRow;
  onSetStatus: (status: 'accepted' | 'rejected') => void;
  onOverrideGrade: (grade: ReviewGrade) => void;
  onVerify: () => void;
  onOpenEvidence: () => void;
  verifying: boolean;
  /** T07/T21 — is this row's Evidence drawer (score breakdown) open? */
  evidenceOpen: boolean;
}): JSX.Element {
  const accepted = row.reviewStatus === 'accepted';
  const rejected = row.reviewStatus === 'rejected';
  // T07: derived from the row itself (pure) — the same builder the AI-check screen
  // uses, so the two screens can never explain one percentage differently.
  const explanation = reviewRowExplanation(row);
  // BUG-5: the display name is now resolved SERVER-SIDE (company.canonicalName →
  // rawName → rawDomain → 'Unnamed company'), mirroring the Evidence-drawer. The
  // raw fields go null once a row resolves to a Company, so the old client-side
  // fallback always showed 'Unnamed company'. Read the server-resolved `name`.
  const name = row.name;
  // E07-F04-T13 — country / city / HQ country as the company-data search reported them.
  const loc = locationCells(row);
  // E07-F01-T11 — the check found nothing to judge by: not a 0% "does not fit".
  const insufficient = insufficientDataBadge(row);
  return (
    <Fragment>
      <tr
        data-testid={`review-row-${row.id}`}
        data-review-status={row.reviewStatus}
        data-grade={row.grade ?? ''}
        data-needs-human={row.needsHuman ? 'true' : 'false'}
        data-check-state={row.checkState}
        data-insufficient-data={row.insufficientData?.reason ?? ''}
        data-reference={row.isReference ? 'true' : 'false'}
        data-rank={row.displayRank ?? ''}
        data-tile={facetKey(row, 'tile')}
        data-industry={facetKey(row, 'industry')}
        data-prefiltered={isPrefiltered(row) ? 'true' : 'false'}
        data-triage-bucket={row.triage?.status === 'done' ? (row.triage.bucket ?? '') : ''}
        className={`${rowTintClass(row)} hover:bg-slate-50 transition border-b border-gray-100`}
      >
        <td className="px-2 py-2.5 text-center">
          <input
            type="checkbox"
            data-testid={`row-select-${row.id}`}
            aria-label={`Select ${name}`}
            checked={selected}
            onChange={() => onToggleSelect?.()}
          />
        </td>
        <td className="px-3 py-2.5 text-xs text-gray-400 text-center" data-testid="row-rank">
          {row.displayRank ?? ''}
        </td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1.5">
            {/* E07-F04-T09: name-or-explicit-marker, never a technical row id. */}
            <CompanyName name={name} />
            {/* E07-F04-T13 — the intake reference company (competitor). The mark lives in
              the row's private discovery provenance, which every check now preserves. */}
            {row.isReference ? (
              <span
                className="text-[9px] bg-amber-100 text-amber-800 border border-amber-300 px-1 rounded whitespace-nowrap"
                data-testid={`reference-flag-${row.id}`}
                title="Reference company — named in the request as a competitor / example"
              >
                Reference
              </span>
            ) : null}
            {/* rationale "why" tooltip (prototype `why` info icon, L894-895) */}
            {row.rationale ? (
              <span
                data-testid={`row-rationale-${row.id}`}
                className="text-slate-300 hover:text-[#D4AF37] text-xs cursor-help"
                title={row.rationale}
                aria-label={`Why: ${row.rationale}`}
              >
                <i className="fa-solid fa-circle-info" aria-hidden />
              </span>
            ) : null}
            {row.needsHuman ? (
              <span
                className="text-amber-500"
                data-testid="needs-human-flag"
                title="Low confidence — needs human review"
              >
                <i className="fa-solid fa-triangle-exclamation" aria-hidden />
              </span>
            ) : null}
            {/* E07-F04-T11: neutral marker — no AI check / Verify has run on this row
              yet, so its criteria are unconfirmed discovery data (NOT verified). */}
            {insufficient ? (
              <span
                className="text-[9px] bg-orange-50 text-orange-700 border border-orange-200 px-1 rounded whitespace-nowrap"
                data-testid={`insufficient-data-flag-${row.id}`}
                title={insufficient.title}
              >
                {insufficient.label}
              </span>
            ) : row.checkState === 'not_checked' ? (
              <span
                className="text-[9px] bg-slate-100 text-slate-500 border border-slate-200 px-1 rounded whitespace-nowrap"
                data-testid={`not-checked-flag-${row.id}`}
                title="Not checked — no AI check or Verify has run for this company yet"
              >
                Not checked
              </span>
            ) : null}
            {/* E06-F05-T16 — the check data were entered by hand (site blocked the AI check). */}
            {row.manualCheck ? (
              <span
                className="text-[9px] bg-sky-50 text-sky-700 border border-sky-200 px-1 rounded whitespace-nowrap"
                data-testid={`manual-check-flag-${row.id}`}
                title={`Checked manually${row.manualCheck.url ? ` — ${row.manualCheck.url}` : ''}`}
              >
                Checked manually
              </span>
            ) : null}
            {/* E06-F07-T01 — stage-0 prefilter: the name looks like a nonprofit /
              distributor / service / staffing firm. A mark with a reason, never a cut. */}
            {(row.prefilterFlags ?? []).map((f, i) => (
              <span
                key={`${f.rule}-${i}`}
                className="text-[9px] bg-violet-50 text-violet-700 border border-violet-200 px-1 rounded whitespace-nowrap"
                data-testid={`prefilter-flag-${row.id}`}
                data-prefilter-rule={f.rule}
                title={f.matched ? `${f.reason} (matched "${f.matched}")` : f.reason}
              >
                {f.reason}
              </span>
            ))}
            {/* E07-F11-T01 — «Return»: clears the flags for this request; the row joins
              the normal list at once (the dismissal survives a prefilter re-run). */}
            {isPrefiltered(row) && onReturnPrefilter ? (
              <button
                type="button"
                data-testid={`prefilter-return-${row.id}`}
                disabled={returning}
                onClick={onReturnPrefilter}
                title="Return the row to the normal list — the prefilter flag is cleared for this request"
                className="text-[9px] px-1 rounded border border-violet-300 text-violet-700 hover:bg-violet-100 whitespace-nowrap disabled:opacity-50"
              >
                <i className="fa-solid fa-rotate-left mr-0.5" aria-hidden />
                {returning ? 'Returning…' : 'Return'}
              </button>
            ) : null}
            {row.isManual ? (
              <span className="text-[9px] bg-slate-200 text-slate-600 px-1 rounded">manual</span>
            ) : null}
            {/* LinkedIn company link (prototype `linkedin` icon, L902) — derived
              from the company name; opens the LinkedIn company search in a new tab. */}
            {!isUnresolvedCompanyName(name) ? (
              <a
                href={`https://www.linkedin.com/company/${encodeURIComponent(name)}`}
                target="_blank"
                rel="noopener"
                data-testid={`row-linkedin-${row.id}`}
                className="text-blue-500 hover:text-blue-700 text-xs"
                title="LinkedIn (new tab)"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="fa-brands fa-linkedin" aria-hidden />
              </a>
            ) : null}
          </div>
          {/* E07-F04-T09: the SERVER-resolved website (companies.domain → rawDomain).
            `rawDomain` alone printed "no website" for every resolved row, because
            the raw column is NULL once the row points at a Company. `?? rawDomain`
            keeps a mid-deploy payload from an older API build readable. */}
          {/* E06-F03-T13-S03: an unknown site reads "Website not found" + inline input. */}
          <ReviewWebsiteCell requestId={requestId} row={row} />
          {/* E07-F11-T03 — Jev: «possibly the same company as X in Library (0.93)». */}
          <SameCompanyHints row={row} onLink={onLinkCompany} linkingCompanyId={linkingCompanyId} />
          {/* E07-F04-T12 — checked in an earlier request: date + link + Refresh. */}
          <PriorCollectionNote
            rowId={row.id}
            prior={row.priorCollection}
            onRefresh={onRefreshPrior}
            refreshDisabled={refreshBusy || rowWebsite(row) === null}
            refreshDisabledReason={rowWebsite(row) === null ? ENTER_WEBSITE_FIRST : undefined}
            onOpenRequest={onOpenRequest}
          />
          {/* E07-F01-T11 — the reason, visible in the row (not only in a tooltip). */}
          {insufficient ? (
            <div
              className="text-[11px] text-orange-700"
              data-testid={`insufficient-data-reason-${row.id}`}
              title={insufficient.title}
            >
              {insufficient.label}: {insufficient.reason}
            </div>
          ) : null}
        </td>
        <td className="px-3 py-2.5 text-xs text-slate-600" data-testid={`row-country-${row.id}`}>
          {loc.country}
        </td>
        <td className="px-3 py-2.5 text-xs text-slate-600" data-testid={`row-city-${row.id}`}>
          {loc.city}
        </td>
        <td className="px-3 py-2.5 text-xs text-slate-600" data-testid={`row-hq-${row.id}`}>
          {loc.hqCountry}
        </td>
        {/* E07-F04-T14 — which search tile found the company + its reported industry. */}
        <td className="px-3 py-2.5 text-xs text-slate-600" data-testid={`row-tile-${row.id}`}>
          {facetLabel(row, 'tile')}
        </td>
        <td className="px-3 py-2.5 text-xs text-slate-600" data-testid={`row-industry-${row.id}`}>
          {facetLabel(row, 'industry')}
        </td>
        <td className="px-3 py-2.5">
          <TriageCell row={row} />
        </td>
        <td className="px-3 py-2.5 text-center">
          <ScoreCell
            score={row.score}
            rowId={row.id}
            explanation={explanation}
            open={evidenceOpen}
            onToggle={onOpenEvidence}
          />
        </td>
        <td className="px-3 py-2.5 text-center" data-testid={`grade-cell-${row.id}`}>
          <GradeChip row={row} onOverride={onOverrideGrade} />
        </td>
        <td className="px-3 py-2.5">
          <MiniBreakdown row={row} />
        </td>
        <td className="px-3 py-2.5 text-center">
          <button
            type="button"
            data-testid={`evidence-btn-${row.id}`}
            onClick={onOpenEvidence}
            title="Open the evidence drawer — sources, quotes, weighted contributions"
            className="text-xs px-2.5 py-1 border border-slate-200 rounded text-slate-600 hover:bg-slate-100 hover:border-slate-300"
          >
            <i className="fa-solid fa-quote-left mr-1 text-[#D4AF37]" aria-hidden /> Evidence
          </button>
          {row.needsHuman || row.checkState === 'not_checked' || insufficient ? (
            <button
              type="button"
              data-testid={`verify-btn-${row.id}`}
              disabled={verifying}
              onClick={onVerify}
              title="Run enrichment + About-Us verification on every criterion"
              className="mt-1 block mx-auto text-[10px] px-2 py-0.5 rounded bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50"
            >
              <i className="fa-solid fa-magnifying-glass-chart mr-1" aria-hidden />
              {verifying ? 'Verifying…' : 'Verify'}
            </button>
          ) : null}
        </td>
        <td className="px-3 py-2.5 text-center whitespace-nowrap">
          <button
            type="button"
            data-testid={`accept-btn-${row.id}`}
            aria-pressed={accepted}
            onClick={() => onSetStatus('accepted')}
            title="Accept"
            className={`w-7 h-7 rounded transition mr-1 ${
              accepted
                ? 'bg-green-500 text-white'
                : 'bg-slate-100 text-slate-400 hover:bg-green-100 hover:text-green-600'
            }`}
          >
            <i className="fa-solid fa-check text-xs" aria-hidden />
          </button>
          <button
            type="button"
            data-testid={`reject-btn-${row.id}`}
            aria-pressed={rejected}
            onClick={() => onSetStatus('rejected')}
            title="Reject"
            className={`w-7 h-7 rounded transition ${
              rejected
                ? 'bg-red-500 text-white'
                : 'bg-slate-100 text-slate-400 hover:bg-red-100 hover:text-red-600'
            }`}
          >
            <i className="fa-solid fa-xmark text-xs" aria-hidden />
          </button>
        </td>
      </tr>
    </Fragment>
  );
}

// ---------------------------------------------------------------------------
// root view
// ---------------------------------------------------------------------------

export function ResearchReviewView({
  requestId,
  onBack,
  onPublish,
  onOpenRequest,
  onBackToList,
  backToListLabel = 'Back to History',
  onBackPrevious,
}: ResearchReviewViewProps): JSX.Element {
  // E07-F04-T18 — the grade filter opens on the request's Grade scope (A | A+B |
  // A+B+C), not on "All": the run already narrowed what counts as a fit.
  const { data: statusData } = useResearchStatus(requestId);
  const gradeScope = statusData?.request?.criteria?.gradeScope;
  const [filter, setFilterState] = useState<ReviewFilterState>(() =>
    initialReviewFilter(gradeScope),
  );
  // The status query may resolve after the first render — apply the scope ONCE,
  // unless the user already picked a grade pill.
  const scopeApplied = useRef(Boolean(gradeScope));
  useEffect(() => {
    if (scopeApplied.current || !gradeScope) return;
    scopeApplied.current = true;
    setFilterState((f) =>
      f.grade === 'all' ? { ...f, grade: 'scope', gradeScope } : { ...f, gradeScope },
    );
  }, [gradeScope]);
  // T05 — pagination: 1-based page + page size. Any filter / sort change goes back
  // to page 1 (setFilter below); paginateRows clamps a page the list shrank under.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<ReviewPageSize>(DEFAULT_REVIEW_PAGE_SIZE);
  const scrollRef = useRef<HTMLDivElement>(null);
  const setFilter = (update: (f: ReviewFilterState) => ReviewFilterState): void => {
    setFilterState(update);
    setPage(1);
  };
  // T01: which long-list row has its evidence drawer open (null → drawer closed).
  // Opening the drawer is a click on the row's "Evidence" button; the drawer
  // composes the shared `Drawer` primitive (scrim, focus-trap, Esc, return-focus).
  const [evidenceRowId, setEvidenceRowId] = useState<number | null>(null);
  // T02: manual add-company / bulk-add dialogs (open flags owned here).
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const toast = useToast();

  const { data, isLoading, isError, error, refetch } = useReviewList(requestId);
  const setStatus = useSetReviewStatus(requestId);
  const override = useOverrideGrade(requestId);
  const verify = useVerifyCompany(requestId);
  const submit = useSubmitReview(requestId);
  // E07-F04-T12 — "Refresh" of an earlier-collected company = the per-row rerun-ai.
  const refreshPrior = useRerunAiOne(requestId);
  // E07-F11-T01 — «Return» a prefilter-flagged row.
  const dismissPrefilter = useDismissPrefilter(requestId);
  // E07-F11-T03 — «Link» a Jev same-company hint.
  const linkCompany = useLinkRowCompany(requestId);
  // E07-F11-T02 — rows ticked for «Re-triage selected».
  const [selectedIds, setSelectedIds] = useState<readonly number[]>([]);
  const toggleSelected = (id: number): void =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  // E07-F11-T02 — once the list has triage results the default order is «check first» on
  // top; any sort click of the person takes over from then on.
  const [sortTouched, setSortTouched] = useState(false);

  /** PAID — one company, one explicit click. */
  function handleRefreshPrior(rowId: number): void {
    refreshPrior.mutate(rowId, {
      onSuccess: () => toast.success('Collecting the data for this company again.'),
      onError: (err) =>
        toast.error(aiRunErrorMessage(err, 'Could not start the refresh — try again.')),
    });
  }

  const allRows = useMemo<readonly ReviewRow[]>(() => data?.rows ?? [], [data?.rows]);
  const effectiveFilter = useMemo(
    () => (sortTouched ? filter : withDefaultTriageSort(filter, allRows)),
    [sortTouched, filter, allRows],
  );
  // E07-F04-T21 — the score explanation shown on top of the Evidence drawer (the
  // Score cell and the Evidence button both open that one drawer).
  const evidenceExplanation = useMemo(() => {
    const row = evidenceRowId == null ? undefined : allRows.find((r) => r.id === evidenceRowId);
    return row ? reviewRowExplanation(row) : null;
  }, [allRows, evidenceRowId]);

  // T05/T08: rank the FULL list (score-monotonic) BEFORE filtering; the surviving
  // rows keep their full-list displayRank so the # never "jumps" on a filter.
  const visibleRows = useMemo(
    () => filteredRankedRows(allRows, effectiveFilter),
    [allRows, effectiveFilter],
  );

  // T05 — the page of the filtered list actually rendered (full-list # kept).
  const pageView = useMemo(
    () => paginateRows(visibleRows, page, pageSize),
    [visibleRows, page, pageSize],
  );
  // E07-F04-T19 — the list scrolls with the PAGE (the shell's `<main>`), not in an
  // inner box. The filter bar is sticky at the top of `<main>`; the table header
  // sticks right under it, so its `top` follows the bar's (wrapping) height.
  const filtersRef = useRef<HTMLDivElement>(null);
  const [filtersHeight, setFiltersHeight] = useState(0);
  useEffect(() => {
    const el = filtersRef.current;
    if (!el) return;
    const measure = (): void => setFiltersHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const goToPage = (next: number): void => {
    setPage(next);
    // Bring the top of the list back into view (under the sticky filter bar) —
    // only when the page is scrolled past it.
    const card = scrollRef.current;
    const scroller = card?.closest('main');
    if (card && scroller) {
      const delta =
        card.getBoundingClientRect().top - scroller.getBoundingClientRect().top - filtersHeight;
      if (delta < 0) scroller.scrollTop += delta;
    }
  };
  const changePageSize = (size: ReviewPageSize): void => {
    setPageSize(size);
    goToPage(1);
  };

  // Count badges always read the FULL list, not the filtered view.
  const summary = useMemo(
    () => computeSummary(allRows, visibleRows.length, data?.summary.discovered ?? allRows.length),
    [allRows, visibleRows.length, data?.summary.discovered],
  );

  const gateOn = (data?.reviewMode ?? 'gate') === 'gate';

  // E07-F04-T18 — rows without a grade (not AI-checked yet) and how many are hidden.
  const ungraded = useMemo(() => ungradedNotice(allRows, filter), [allRows, filter]);

  const setGrade = (g: ReviewGradeFilter): void => setFilter((f) => ({ ...f, grade: g }));
  const setStatusFilter = (s: ReviewStatusFilter): void => setFilter((f) => ({ ...f, status: s }));
  // T14: the Score toggle is one of three sort keys — a click from a facet sort goes
  // back to the score (high → low); from the score sort it flips the direction.
  const sortFrom = (f: ReviewFilterState): ReviewFilterState =>
    sortTouched ? f : withDefaultTriageSort(f, allRows);
  const flipSort = (): void => {
    setSortTouched(true);
    setFilter((f) => nextSort(sortFrom(f), 'score'));
  };
  const sortBy = (by: ReviewSortBy): void => {
    setSortTouched(true);
    setFilter((f) => nextSort(sortFrom(f), by));
  };
  // E07-F11-T02 — the triage bucket filter (combines with every other filter).
  const triaged = useMemo(() => hasTriage(allRows), [allRows]);
  const triageOptions = useMemo(() => triageFilterOptions(allRows), [allRows]);
  const setTriageFilter = (value: ReviewTriageFilter): void =>
    setFilter((f) => ({ ...f, triage: value }));
  const visibleIds = useMemo(() => pageView.rows.map((r) => r.id), [pageView.rows]);
  const allPageSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const togglePageSelected = (): void =>
    setSelectedIds((ids) =>
      allPageSelected
        ? ids.filter((id) => !visibleIds.includes(id))
        : [...ids, ...visibleIds.filter((id) => !ids.includes(id))],
    );
  const setFacet = (facet: ReviewFacet, value: string): void =>
    setFilter((f) => ({ ...f, [facet]: value }));
  // E07-F11-T01 — the «prefiltered» filter (combines with every other filter).
  const prefiltered = useMemo(() => prefilteredCount(allRows), [allRows]);
  const togglePrefilter = (): void =>
    setFilter((f) => ({ ...f, prefilter: f.prefilter === 'flagged' ? 'all' : 'flagged' }));
  const tileOptions = useMemo(() => facetOptions(allRows, 'tile'), [allRows]);
  const industryOptions = useMemo(() => facetOptions(allRows, 'industry'), [allRows]);

  return (
    // E07-F04-T15 + E07-F04-T19 — ONE scrollbar, and it is the page's: the view
    // has no height cap and the list has no inner scroll box, so the shell's
    // `<main>` is the only scroll container (T15's capped inner box squeezed an
    // 891-row list into a tiny window — T19). The filter bar and the table header
    // stay reachable via `position: sticky` against `<main>`.
    <div data-testid="research-review-view" className="flex flex-col">
      {onBack || onBackToList || onBackPrevious ? (
        // E06-F02-T11/T12 — the "back" affordances share one row inside the view.
        <div className="mb-4 shrink-0 self-start flex items-center gap-5">
          {onBackPrevious ? (
            <button
              type="button"
              data-testid="research-back"
              onClick={onBackPrevious}
              className="text-sm text-slate-500 hover:text-slate-800 flex items-center"
            >
              <i className="fa-solid fa-arrow-left mr-2" aria-hidden /> Back
            </button>
          ) : null}
          {onBackToList ? (
            <button
              type="button"
              data-testid="research-back-to-library"
              onClick={onBackToList}
              className="text-sm text-slate-500 hover:text-slate-800 flex items-center"
            >
              <i className="fa-solid fa-arrow-left mr-2" aria-hidden /> {backToListLabel}
            </button>
          ) : null}
          {onBack ? (
            <button
              type="button"
              data-testid="review-back"
              onClick={onBack}
              className="text-sm text-slate-500 hover:text-slate-800 flex items-center"
            >
              <i className="fa-solid fa-arrow-left mr-2" aria-hidden /> Back to run
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="shrink-0">
        {/* E07-F04-T16 — the criteria the request was submitted with (geo / industry /
            size / location stages / Avoid), so a multi-country list can be read
            against what was actually asked. Same recap as the Run screen. */}
        <ReviewRequestCriteria requestId={requestId} />
        {/* E07-F02-T11 — some discovery searches failed: say which and why. */}
        <ReviewDiscoveryWarning requestId={requestId} />
        {/* E07-F02-T17 — per-tile found / duplicates / new + why a tile added nothing. */}
        <ReviewDiscoveryTiles requestId={requestId} />
        {/* E07-F02-T18 — companies cut at search (DNS company / DNS industry / size). */}
        <ReviewDiscoveryExclusions requestId={requestId} />
      </div>

      {/* summary strip (T05 count badges) */}
      <div
        data-testid="review-summary"
        className="shrink-0 bg-white rounded-lg shadow-sm border border-gray-200 mb-4 px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-2"
      >
        <div>
          <span className="text-2xl font-bold text-slate-900" data-testid="summary-total">
            {summary.total}
          </span>{' '}
          <span className="text-xs text-gray-400 uppercase">on long-list</span>
        </div>
        <div className="text-slate-300">|</div>
        <div>
          <span className="text-lg font-bold text-green-600" data-testid="summary-accepted">
            {summary.accepted}
          </span>{' '}
          <span className="text-xs text-gray-400">accepted</span>
        </div>
        <div>
          <span className="text-lg font-bold text-amber-600" data-testid="summary-needs-human">
            {summary.needsHuman}
          </span>{' '}
          <span className="text-xs text-gray-400">needs-human</span>
        </div>
        {/* E07-F04-T11: unchecked rows counted separately from needs-human. */}
        <div>
          <span className="text-lg font-bold text-slate-500" data-testid="summary-not-checked">
            {summary.notChecked}
          </span>{' '}
          <span className="text-xs text-gray-400">not checked</span>
        </div>
        <div className="text-xs text-gray-400" data-testid="summary-showing">
          showing <b>{summary.showing}</b> of {summary.total} ({summary.discovered} discovered)
        </div>
        <div className="ml-auto flex items-center gap-2">
          {gateOn ? (
            <span
              data-testid="gate-badge"
              title="A specialist must approve this long-list before it can be published."
              className="text-[10px] bg-amber-100 text-amber-700 px-2 py-1 rounded-full"
            >
              <i className="fa-solid fa-shield-halved mr-1" aria-hidden />
              Specialist gate ON
            </span>
          ) : (
            <span
              data-testid="auto-badge"
              title="No specialist gate — the ranked list is delivered automatically."
              className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-full"
            >
              <i className="fa-solid fa-bolt mr-1" aria-hidden />
              Auto-deliver
            </span>
          )}
          {/* T02 — manual add + bulk add (prototype Add company / Bulk add) */}
          <button
            type="button"
            data-testid="review-add-company"
            onClick={() => setAddOpen(true)}
            className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded text-xs hover:bg-slate-50"
          >
            <i className="fa-solid fa-plus mr-1" aria-hidden /> Add company
          </button>
          <button
            type="button"
            data-testid="review-bulk-add"
            onClick={() => setBulkOpen(true)}
            className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded text-xs hover:bg-slate-50"
          >
            <i className="fa-solid fa-layer-group mr-1" aria-hidden /> Bulk add
          </button>
          {gateOn ? (
            <button
              type="button"
              data-testid="review-submit"
              disabled={submit.isPending}
              // E07-F04-T24 — this button only closes the review gate and opens
              // the Publish screen; nothing is sent to the recruiter here (that
              // happens on Publish). Label + toast must not claim a send.
              title="Finish the review and go to the Publish screen — the companies are sent to the recruiter from there"
              onClick={() =>
                submit.mutate(undefined, {
                  onSuccess: () => {
                    // Announce the successful gate-submit BEFORE the screen flips
                    // to Publish — a silent screen change was found confusing.
                    toast.success('Review finished — send the companies from the Publish screen');
                    onPublish?.();
                  },
                  onError: (err) =>
                    toast.error(err.message || 'Could not finish the review — please try again.'),
                })
              }
              className="px-4 py-1.5 bg-[#D4AF37] text-slate-900 rounded text-xs font-bold hover:bg-[#B5952F] transition disabled:opacity-50"
            >
              <i className="fa-solid fa-share-from-square mr-1" aria-hidden />
              {submit.isPending ? 'Saving…' : 'Continue to publish'}
            </button>
          ) : (
            <button
              type="button"
              data-testid="review-publish"
              onClick={() => onPublish?.()}
              className="px-4 py-1.5 bg-[#D4AF37] text-slate-900 rounded text-xs font-bold hover:bg-[#B5952F] transition"
            >
              <i className="fa-solid fa-share-from-square mr-1" aria-hidden />
              Publish
            </button>
          )}
        </div>
      </div>

      {/* E07-F11-T02 — Jev triage: button + confirmation, re-triage selected, progress. */}
      <ReviewTriageBar
        requestId={requestId}
        selectedIds={selectedIds}
        onClearSelection={() => setSelectedIds([])}
      />

      {/* filters (T05 — Grade + Status combine; score-sort toggle) */}
      <div
        ref={filtersRef}
        data-testid="review-filters"
        className="sticky top-0 z-[2] shrink-0 bg-white rounded-t-lg shadow-sm border-x border-t border-gray-200 px-4 py-2.5 flex flex-wrap items-center gap-2"
      >
        <span className="text-xs font-bold text-slate-400 uppercase mr-1">Grade</span>
        {filter.gradeScope ? (
          <FilterPill
            label={`Scope ${filter.gradeScope}`}
            active={filter.grade === 'scope'}
            testId="grade-filter-scope"
            onClick={() => setGrade('scope')}
          />
        ) : null}
        <FilterPill
          label="All"
          active={filter.grade === 'all'}
          testId="grade-filter-all"
          onClick={() => setGrade('all')}
        />
        <FilterPill
          label="A"
          active={filter.grade === 'A'}
          testId="grade-filter-A"
          onClick={() => setGrade('A')}
        />
        <FilterPill
          label="B"
          active={filter.grade === 'B'}
          testId="grade-filter-B"
          onClick={() => setGrade('B')}
        />
        <FilterPill
          label="C"
          active={filter.grade === 'C'}
          testId="grade-filter-C"
          onClick={() => setGrade('C')}
        />
        {/* E07-F04-T18 — rows with no score/grade yet (AI check has not run). */}
        <FilterPill
          label={`No grade (${ungraded.ungraded})`}
          active={filter.grade === 'ungraded'}
          testId="grade-filter-ungraded"
          onClick={() => setGrade('ungraded')}
        />
        <span className="text-xs font-bold text-slate-400 uppercase ml-3 mr-1">Status</span>
        <FilterPill
          label="All"
          active={filter.status === 'all'}
          testId="status-filter-all"
          onClick={() => setStatusFilter('all')}
        />
        <FilterPill
          label="Accepted"
          active={filter.status === 'accepted'}
          testId="status-filter-accepted"
          onClick={() => setStatusFilter('accepted')}
        />
        <FilterPill
          label="Rejected"
          active={filter.status === 'rejected'}
          testId="status-filter-rejected"
          onClick={() => setStatusFilter('rejected')}
        />
        <FilterPill
          label="Pending"
          active={filter.status === 'pending'}
          testId="status-filter-pending"
          onClick={() => setStatusFilter('pending')}
        />
        {/* E07-F11-T01 — only the rows a stage-0 prefilter marked (by default they sit
            at the end of the list). Shown when some row is flagged, or while active. */}
        {prefiltered > 0 || filter.prefilter === 'flagged' ? (
          <FilterPill
            label={`Prefiltered (${prefiltered})`}
            active={filter.prefilter === 'flagged'}
            testId="prefilter-filter"
            onClick={togglePrefilter}
          />
        ) : null}
        {/* E07-F11-T02 — the Jev triage buckets (only once some row has a triage). */}
        {triaged || (filter.triage ?? TRIAGE_FILTER_ALL) !== TRIAGE_FILTER_ALL ? (
          <label className="flex items-center gap-1 ml-3">
            <span className="text-xs font-bold text-slate-400 uppercase mr-1">Triage</span>
            <select
              data-testid="triage-filter"
              value={filter.triage ?? TRIAGE_FILTER_ALL}
              onChange={(e) => setTriageFilter(e.target.value as ReviewTriageFilter)}
              className={`text-xs border rounded px-2 py-1 ${
                (filter.triage ?? TRIAGE_FILTER_ALL) === TRIAGE_FILTER_ALL
                  ? 'border-gray-200 text-slate-600 bg-white'
                  : 'border-slate-800 text-slate-900 bg-slate-50 font-medium'
              }`}
            >
              {triageOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label} ({o.count})
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {/* T14 — narrow a 240-440-row long-list to one search tile / industry. */}
        <FacetSelect
          facet="tile"
          label="Tile"
          value={filter.tile ?? REVIEW_FACET_ALL}
          options={tileOptions}
          onChange={(v) => setFacet('tile', v)}
        />
        <FacetSelect
          facet="industry"
          label="Industry"
          value={filter.industry ?? REVIEW_FACET_ALL}
          options={industryOptions}
          onChange={(v) => setFacet('industry', v)}
        />
        <button
          type="button"
          data-testid="review-sort-toggle"
          data-sort-dir={effectiveFilter.sortDir}
          data-active={(effectiveFilter.sortBy ?? 'score') === 'score' ? 'true' : 'false'}
          onClick={flipSort}
          className={`ml-auto text-xs px-3 py-1 border rounded hover:bg-slate-50 ${
            (effectiveFilter.sortBy ?? 'score') === 'score'
              ? 'border-slate-400 text-slate-800'
              : 'border-gray-200 text-slate-500'
          }`}
        >
          <i
            className={`fa-solid mr-1 ${
              effectiveFilter.sortDir === 'desc' || (effectiveFilter.sortBy ?? 'score') !== 'score'
                ? 'fa-arrow-down-wide-short'
                : 'fa-arrow-up-short-wide'
            }`}
            aria-hidden
          />{' '}
          Score
        </button>
      </div>

      {/* E07-F04-T18 — the grade filter hides rows without a grade: say so, and that
          the ones with no score have not been through the AI check yet. */}
      {ungraded.hidden > 0 ? (
        <div
          data-testid="review-ungraded-notice"
          className="shrink-0 bg-slate-50 border-x border-t border-gray-200 px-4 py-2 text-xs text-slate-600 flex flex-wrap items-center gap-2"
        >
          <i className="fa-solid fa-circle-info text-slate-400" aria-hidden />
          <span>
            <b data-testid="review-ungraded-hidden">{ungraded.hidden}</b>{' '}
            {ungraded.hidden === 1 ? 'company has' : 'companies have'} no grade and{' '}
            {ungraded.hidden === 1 ? 'is' : 'are'} hidden by the grade filter
            {ungraded.notChecked > 0 ? (
              <>
                {' '}
                — <b data-testid="review-ungraded-not-checked">{ungraded.notChecked}</b> of them
                have not passed the AI check yet (no score)
              </>
            ) : null}
            .
          </span>
          <button
            type="button"
            data-testid="review-ungraded-show"
            onClick={() => setGrade('ungraded')}
            className="text-xs underline text-slate-700 hover:text-slate-900"
          >
            Show them
          </button>
        </div>
      ) : null}

      {/* breakdown legend (T05 — explains the 6 squares) */}
      <div
        data-testid="review-legend"
        className="shrink-0 bg-white border-x border-gray-200 px-4 py-2 text-[11px] text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-1"
      >
        <span className="font-bold uppercase text-slate-400 mr-1">Breakdown</span>
        <span className="text-gray-400">6 squares, left → right:</span>
        {REVIEW_LEGEND.map((l) => (
          <span
            key={l.key}
            className="px-1.5 py-0.5 bg-slate-50 rounded"
            data-testid={`legend-${l.key}`}
          >
            <b className="text-slate-700">{l.short}</b> {l.label}
          </span>
        ))}
        <span className="ml-1 pl-2 border-l border-gray-200">
          <span className="text-green-500">✓</span> pass · <span className="text-amber-500">◑</span>{' '}
          partial · <span className="text-red-500">✗</span> fail
        </span>
      </div>

      {/* table (sticky header; 100-440-row lists are paged below).
          E07-F04-T19 — NO overflow on the card or on `review-scroll`: any
          `overflow` other than visible makes that box the sticky container and
          (a) brings back the tiny inner scroll window, (b) stops the header from
          sticking against `<main>`. The header sticks right under the sticky
          filter bar (`top` = its measured height). */}
      <div className="flex flex-col bg-white rounded-b-lg shadow-sm border border-gray-200">
        <div ref={scrollRef} data-testid="review-scroll">
          <table className="w-full text-left" data-testid="review-table">
            <thead
              className="bg-slate-50 text-slate-500 uppercase font-bold text-xs sticky z-[1]"
              style={{ top: filtersHeight }}
            >
              <tr>
                <th className="px-2 py-3 text-center w-8">
                  <input
                    type="checkbox"
                    data-testid="review-select-page"
                    aria-label="Select the rows of this page"
                    checked={allPageSelected}
                    onChange={togglePageSelected}
                  />
                </th>
                <th className="px-3 py-3 text-center w-10">#</th>
                <th className="px-3 py-3">Company</th>
                <th className="px-3 py-3 w-24" title="Country of the company (company-data search)">
                  Country
                </th>
                <th className="px-3 py-3 w-24" title="City of the company address">
                  City
                </th>
                <th className="px-3 py-3 w-24" title="Country of the headquarters">
                  HQ
                </th>
                <th
                  className="px-3 py-3 w-40"
                  title="The discovery search that found the company: sub-industry · geo · size"
                >
                  <SortHeader by="tile" label="Tile" filter={effectiveFilter} onSort={sortBy} />
                </th>
                <th
                  className="px-3 py-3 w-32"
                  title="Industry of the company as the company-data search reported it"
                >
                  <SortHeader
                    by="industry"
                    label="Industry"
                    filter={effectiveFilter}
                    onSort={sortBy}
                  />
                </th>
                <th
                  className="px-3 py-3 w-36"
                  title="Jev triage bucket + the short reason (check first / check / doubtful)"
                >
                  <SortHeader by="triage" label="Triage" filter={effectiveFilter} onSort={sortBy} />
                </th>
                <th
                  className="px-3 py-3 text-center w-16"
                  /* T07 — the column-level answer; the per-row click gives the full
                     weighted breakdown. */
                  title="Weighted % of the request criteria the AI check could confirm with a verbatim quote from the company site. Click any score for the breakdown — a low number usually means 'not proven', not 'does not match'."
                >
                  Score
                </th>
                <th className="px-3 py-3 text-center w-16">Grade</th>
                <th
                  className="px-3 py-3"
                  title="Industry · Sub-industry · Geo · Size · Founded · Services"
                >
                  Breakdown
                </th>
                <th className="px-3 py-3 text-center w-24">Evidence</th>
                <th className="px-3 py-3 text-center w-28">Accept / Reject</th>
              </tr>
            </thead>
            <tbody>
              {isError ? (
                /* BUG-4: a failed `GET review` must be VISUALLY DISTINCT from a
                   genuinely empty list — the shared empty-state masked real
                   backend failures. Show an error banner + Retry, never the
                   "No companies match" copy. */
                <tr>
                  <td
                    colSpan={REVIEW_TABLE_COLSPAN}
                    className="px-3 py-10 text-center"
                    data-testid="review-error"
                  >
                    <div className="mx-auto flex max-w-md flex-col items-center gap-3 text-sm text-red-600">
                      <span>
                        <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden />
                        Could not load the review list
                        {error?.message ? (
                          <span className="text-red-500"> — {error.message}</span>
                        ) : null}
                      </span>
                      <button
                        type="button"
                        data-testid="review-error-retry"
                        onClick={() => void refetch()}
                        className="rounded border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        <i className="fa-solid fa-rotate-right mr-1" aria-hidden /> Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visibleRows.length > 0 ? (
                pageView.rows.map((row) => (
                  <ReviewTableRow
                    key={row.id}
                    requestId={requestId}
                    row={row}
                    onSetStatus={(status) => setStatus.mutate({ rowId: row.id, status })}
                    onOverrideGrade={(grade) => override.mutate({ rowId: row.id, grade })}
                    onVerify={() =>
                      verify.mutate(row.id, {
                        onError: (err) =>
                          toast.error(
                            err.message ||
                              'Verification failed — the enrichment run did not complete.',
                          ),
                      })
                    }
                    onOpenEvidence={() => setEvidenceRowId(row.id)}
                    verifying={verify.isPending && verify.variables === row.id}
                    evidenceOpen={evidenceRowId === row.id}
                    onRefreshPrior={() => handleRefreshPrior(row.id)}
                    refreshBusy={refreshPrior.isPending}
                    onOpenRequest={onOpenRequest}
                    onReturnPrefilter={() =>
                      dismissPrefilter.mutate(
                        { rowId: row.id },
                        {
                          onError: (err) =>
                            toast.error(err.message || 'Could not return the row — try again.'),
                        },
                      )
                    }
                    returning={
                      dismissPrefilter.isPending && dismissPrefilter.variables?.rowId === row.id
                    }
                    selected={selectedIds.includes(row.id)}
                    onToggleSelect={() => toggleSelected(row.id)}
                    onLinkCompany={(companyId) =>
                      linkCompany.mutate(
                        { rowId: row.id, companyId },
                        {
                          onSuccess: () => toast.success('Row linked to the Library company.'),
                          onError: (err) =>
                            toast.error(err.message || 'Could not link the row — try again.'),
                        },
                      )
                    }
                    linkingCompanyId={
                      linkCompany.isPending && linkCompany.variables?.rowId === row.id
                        ? linkCompany.variables.companyId
                        : null
                    }
                  />
                ))
              ) : (
                <tr>
                  <td
                    colSpan={REVIEW_TABLE_COLSPAN}
                    className="px-3 py-10 text-center text-sm text-gray-400"
                    data-testid="review-empty"
                  >
                    {isLoading ? 'Loading…' : 'No companies match these filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* T05 — pager: only when the filtered list is longer than the smallest page. */}
        {visibleRows.length > REVIEW_PAGE_SIZES[0] ? (
          <ReviewPager
            view={pageView}
            pageSize={pageSize}
            onPage={goToPage}
            onPageSize={changePageSize}
          />
        ) : null}

        {/* bottom legend footer (prototype L995-1004) — verdict + entity + grade
            glossary for the whole table. */}
        <div
          data-testid="review-legend-footer"
          className="px-4 py-2 bg-slate-50 border-t border-gray-100 text-[11px] text-gray-400 flex flex-wrap items-center gap-x-4 gap-y-1"
        >
          <span>
            <i className="fa-solid fa-check text-green-500" aria-hidden /> PASS
          </span>
          <span>
            <i className="fa-solid fa-circle-half-stroke text-amber-500" aria-hidden /> PARTIAL
          </span>
          <span>
            <i className="fa-solid fa-xmark text-red-500" aria-hidden /> FAIL
          </span>
          <span>
            <i className="fa-solid fa-triangle-exclamation text-amber-500" aria-hidden /> needs
            human
          </span>
          <span>
            <i className="fa-solid fa-sitemap text-slate-400" aria-hidden /> division
          </span>
          <span>
            <i className="fa-solid fa-box-archive text-slate-400" aria-hidden /> portfolio item
          </span>
          <span className="pl-2 border-l border-gray-200">
            Grade = fit to your request: <b className="text-green-700">A</b> direct ·{' '}
            <b className="text-blue-700">B</b> broader · <b className="text-slate-500">C</b>{' '}
            long-list filler
          </span>
        </div>
      </div>

      {/* T01 — the evidence drawer (slide-over). Mounted once; `rowId=null` keeps
          it closed/unmounted. The "Evidence" button on each row sets the open
          row; Esc / scrim / Close clears it (and return-focus lands on the row's
          button — owned by the shared Drawer primitive). */}
      <EvidenceDrawer
        requestId={requestId}
        rowId={evidenceRowId}
        onClose={() => setEvidenceRowId(null)}
        explanation={evidenceExplanation}
      />

      {/* T02 — manual add-company / bulk-add dialogs (reuse the review api). */}
      <AddCompanyDialog requestId={requestId} open={addOpen} onClose={() => setAddOpen(false)} />
      <BulkAddDialog requestId={requestId} open={bulkOpen} onClose={() => setBulkOpen(false)} />
    </div>
  );
}

export default ResearchReviewView;
